import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  Typography,
  ExpandMoreIcon,
  AddIcon,
  DeleteIcon,
  GridRowSelectionModel,
} from "../../../../shared/utils/muiImports";
import UserForm from "../components/UserForm";
import UserList from "../components/UsersList";
import useUsers from "../../../../hooks/user/useUser";
import useUserForm from "../../../../hooks/user/useUserForm";
import { User } from "../User.type";
import { toast } from "react-toastify";
import DeleteConfirmationDialog from "../../../../shared/components/DeleteConfirmationDialog";

const UserPage: React.FC = () => {
  const emptyUser: User = {
    id: 0,
    patientId: "",
    personalDetailsId: 0,
    contactDetailsId: 0,
    admissionDetailsId: 0,
    isActive: true,
    firstName: "",
    middleName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "",
    maritalStatus: "",
    govtIdProof: "",
    idProofNumber: "",
    bloodGroup: "",
    email: "",
    mobileNumber: "",
    alternateNumber: "",
    address: "",
    country: "",
    state: "",
    city: "",
    hospitalLocation: null,
    admissionDate: null,
    doctor: "",
    expectedDischargeDate: null,
    dischargeDate: null,
    consultationDateTime: null,
  };

  const [userListTitle] = useState("List of Users");
  const [userFormTitle, setUserFormTitle] = useState("User");

  const [isUserListExpanded, setIsUserListExpanded] = useState(true);
  const [isUserFormExpanded, setIsUserFormExpanded] = useState(false);
  const { users, editUser, createUser, removeUser } = useUsers();
  const {
    setCurrentUser,
    currentUser,
    handleInputChange,
    handleSave,
    errors,
    setErrors,
    handleRowClick,
  } = useUserForm(emptyUser);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(0);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    []
  );

  const [activeStep, setActiveStep] = useState<number>(0);

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setSelectionModel(newSelection);
  };

  const handleCreateNewUser = () => {
    setUserFormTitle("Create a User");
    setCurrentUser(emptyUser);
    manageAccordion(true);
    setActiveStep(0);
  };

  const manageAccordion = (isExpanded: boolean) => {
    setIsUserFormExpanded(isExpanded);
    setIsUserListExpanded(!isExpanded);
  };

  const handleSaveClick = (e: React.FormEvent<HTMLFormElement>) => {
    handleSave((user: User) => {
      if (user.id) {
        editUser(currentUser)
          .then(() => {
            toast.success("User updated successfully!");
            manageAccordion(false);
          })
          .catch(() => {
            toast.error("Failed to update User");
            manageAccordion(true);
          });
      } else {
        createUser(currentUser)
          .then(() => {
            toast.success("User added successfully!");
            manageAccordion(false);
          })
          .catch(() => {
            toast.error("Failed to add User");
            manageAccordion(true);
          });
      }
      handleCreateNewUser();
    });
  };

  const handleDeleteRequest = () => {
    if (!selectionModel.length) {
      toast.error("No user selected. Please select a user from the grid.");
      return;
    }

    setDeleteId(Number(selectionModel[0]));
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteId(0);
    setIsDeleteDialogOpen(false);
  };

  const handleDelete = () => {
    removeUser(deleteId)
      .then(() => toast.success("User deleted successfully!"))
      .catch(() => toast.error("Failed to delete User"));
    setIsDeleteDialogOpen(false);
    handleCreateNewUser();
    manageAccordion(false);
  };

  const handleRowSelect = (e: any) => {
    setUserFormTitle("Update User");
    manageAccordion(true);
    handleRowClick(e);
  };

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      if (panel === "UserList") {
        manageAccordion(false);
      }

      if (panel === "UserForm") {
        manageAccordion(true);
      }
    };

  const handleCancel = () => {
    setIsUserFormExpanded(false);
    setIsUserListExpanded(true);
    setCurrentUser(emptyUser);
    setUserFormTitle("User");
    setErrors({ contactDetails: {}, personalDetails: {} });
  };

  return (
    <Box sx={{ padding: 0 }}>
      <Grid item xs={12} sx={{ padding: 1 }}>
        <Accordion
          expanded={isUserListExpanded}
          onChange={handleAccordionChange("UserList")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="user-list-content"
            id="user-list-header"
          >
            <Typography>{userListTitle}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <UserList
              users={users}
              onUserSelect={handleRowSelect}
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={handleSelectionChange}
            />
            <Grid item xs={12} sx={{ pt: 2 }}>
              <Button
                sx={{ mr: 1 }}
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleCreateNewUser}
              >
                Add
              </Button>
              <Button
                sx={{ mr: 1 }}
                variant="outlined"
                color="secondary"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteRequest}
              >
                Delete
              </Button>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>

      <Grid item xs={12} sx={{ padding: 1 }}>
        <Accordion
          expanded={isUserFormExpanded}
          onChange={handleAccordionChange("UserForm")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="user-form-content"
            id="user-form-header"
          >
            <Typography>{userFormTitle}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <UserForm
              setErrors={setErrors}
              user={currentUser}
              errors={errors}
              onChange={handleInputChange}
              onSave={handleSaveClick}
              onCancel={handleCancel}
              activeStep={activeStep}
              setActiveStep={setActiveStep}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>

      <DeleteConfirmationDialog
        message={`Are you sure you want to delete this User?`}
        open={isDeleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDelete}
        entityName={"User"}
      />
    </Box>
  );
};

export default UserPage;
